import { AfterViewInit, ChangeDetectionStrategy, Component } from '@angular/core';
import { Router } from '@angular/router';
import { Chart } from 'angular-highcharts';
import { add, addWeeks, format } from 'date-fns';
import { take, takeUntil } from 'rxjs/operators';
import { CompanyService } from '../../root/company.service';
import { NetworkableDestroyableComponent } from '../../shared/networkable-destroyable.component';
import { RevenueDay } from '../../types';
import { ProfitMissedType, ProfitType } from './model';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

@Component({
  selector: 'lohi-driver-kpi-total-revenue-chart',
  templateUrl: './driver-kpi-total-revenue-chart.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriverKpiTotalRevenueChartComponent extends NetworkableDestroyableComponent implements AfterViewInit {
  public readonly chart: Chart;

  // todo: move revenue data to settlements service
  constructor(private companyService: CompanyService, private router: Router) {
    super();
    this.chart = this.createChart();
  }

  public ngAfterViewInit() {
    this.setChartDataSubscription();
  }

  private setChartDataSubscription(): void {
    this.companyService.revenueData$.pipe(takeUntil(this.destroy$$)).subscribe({
      next: (data) => {
        if (!data?.days) {
          console.warn('No days data received:', data);
          return;
        }
        this.updateChartData(data.days);
      },
    });
  }

  private createChart(): Chart {
    return new Chart({
      chart: {
        type: 'column',
        backgroundColor: '#2c3a4e',
      },

      plotOptions: {
        column: {
          cursor: 'pointer',
          stacking: 'normal',
          opacity: 0.9,
          pointPadding: -0.1,
          borderWidth: 1,
          borderColor: '#2c3a4e',
          dataLabels: {
            enabled: false,
          },
        },
      },

      title: {
        text: '',
      },

      xAxis: {
        type: 'datetime',
        visible: true,
        width: '100%',
        minPadding: 0,
        maxPadding: 0,
        labels: {
          style: {
            color: '#95A2B3',
          },
          formatter() {
            const a = new Date(this.value);
            const b = addWeeks(new Date(this.value), 1);
            return `${format(a, 'MMM dd')} - ${format(b, 'MMM dd')}`;
          },
        },
        title: {
          text: 'Date',
          style: {
            color: '#95A2B3',
          },
        },
      },

      yAxis: {
        title: {
          text: 'Total Revenue ($)',
          style: {
            color: '#95A2B3',
          },
        },
        labels: {
          style: {
            color: '#95A2B3',
          },
          formatter() {
            return `${currencyFormatter.format(this.value as number)}`;
          },
        },
        gridLineColor: 'rgba(255,255,255,0.16)',
        stackLabels: {
          enabled: true,
          style: {
            color: '#95A2B3',
            textOutline: 'none',
          },
          formatter() {
            if ((this as any).stack === 'Revenue Loss') {
              return `<text style="color: rgb(255, 0, 0)">${currencyFormatter.format(this.total as number)}</text>`;
            }
            return `${currencyFormatter.format(this.total as number)}`;
          },
        },
      },

      tooltip: {
        enabled: true,
        shared: false,
        pointFormatter() {
          return `${new Date(this.x).toLocaleDateString()} <span class="font-semibold">${currencyFormatter.format(
            this.y,
          )}</span><br>`;
        },
      },

      noData: {
        position: {
          align: 'center',
          verticalAlign: 'middle',
        },
      },

      legend: {
        enabled: false,
      },

      credits: {
        enabled: false,
      },

      exporting: {
        enabled: false,
      },
    });
  }

  private updateChartData(data: RevenueDay[]): void {
    this.chart?.ref$.pipe(take(1)).subscribe((chart) => {
      while (chart.series.length > 0) {
        chart.series[0].remove(false, false);
      }
      if (!data) {
        return;
      }

      const dabData = [];
      const otherData = [];
      const patienceAndDetentionData = [];
      const loadData = [];

      for (const day of data) {
        const x = add(new Date(day.date), { minutes: new Date().getTimezoneOffset() }).getTime();
        dabData.push({
          label: 'DAB',
          name: 'DAB',
          color: profitTypeColorMap.dab,
          x,
          y: day.dedicatedAssetBonusCents / 100.0,
          custom: {
            settlementStatementId: day.settlementDetailId,
          },
        });
        otherData.push({
          label: 'Other',
          name: 'Other',
          color: profitTypeColorMap.other,
          x,
          y: day.otherCents / 100.0,
          custom: {
            settlementStatementId: day.settlementDetailId,
          },
        });
        patienceAndDetentionData.push({
          label: 'Detention And Patience Pay',
          name: 'Detention And Patience Pay',
          color: profitTypeColorMap.patience_pay_detention,
          x,
          y: day.detentionCents / 100.0,
          custom: {
            settlementStatementId: day.settlementDetailId,
          },
        });
        loadData.push({
          label: 'Loads',
          name: 'Loads',
          color: profitTypeColorMap.load,
          x,
          y: (day.loadCents + day.takeRateCents) / 100.0,
          custom: {
            settlementStatementId: day.settlementDetailId,
          },
        });
      }

      chart.addSeries(
        {
          type: 'column',
          name: 'Revenue',
          stack: 'Revenue',
          data: dabData,
          events: {
            click: (event) => {
              const point = event.point as any;
              const settlementStatementId = point.custom.settlementStatementId;
              if (!settlementStatementId) {
                return;
              }
              this.router.navigate(['/settlements', 'details', settlementStatementId]);
            },
          },
        },
        false,
        false,
      );

      chart.addSeries(
        {
          type: 'column',
          name: 'Revenue',
          stack: 'Revenue',
          data: otherData,
          events: {
            click: (event) => {
              const point = event.point as any;
              const settlementStatementId = point.custom.settlementStatementId;
              if (!settlementStatementId) {
                return;
              }
              this.router.navigate(['/settlements', 'details', settlementStatementId]);
            },
          },
        },
        false,
        false,
      );

      chart.addSeries(
        {
          type: 'column',
          name: 'Revenue',
          stack: 'Revenue',
          data: patienceAndDetentionData,
          events: {
            click: (event) => {
              const point = event.point as any;
              const settlementStatementId = point.custom.settlementStatementId;
              if (!settlementStatementId) {
                return;
              }
              this.router.navigate(['/settlements', 'details', settlementStatementId]);
            },
          },
        },
        false,
        false,
      );

      chart.addSeries(
        {
          type: 'column',
          name: 'Revenue',
          stack: 'Revenue',
          data: loadData,
          events: {
            click: (event) => {
              const point = event.point as any;
              const settlementStatementId = point.custom.settlementStatementId;
              if (!settlementStatementId) {
                return;
              }
              this.router.navigate(['/settlements', 'details', settlementStatementId]);
            },
          },
        },
        true,
        true,
      );
    });
  }
}

/* eslint-disable @typescript-eslint/naming-convention */
export const profitTypeColorMap: Record<ProfitType | ProfitMissedType, string> = {
  load: '#fafafa',
  dab: '#c8c8c8',
  patience_pay_detention: '#969696',
  other: '#818181',
  dab_loss: '#8c3232',
  take_rate_loss: '#ef6048',
};

/* eslint-enable @typescript-eslint/naming-convention */
